import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { LogoIcon } from '../Logo';
import { Loader2, CheckCircle2, XCircle, ArrowLeft } from 'lucide-react';
import { api } from '../../lib/api';
import { FestiveDecoration } from '../FestiveDecoration';
import { toast } from 'sonner';

interface VerifyScreenProps {
  token: string;
  contact: string;
  onSuccess: () => void;
  onNeedRegistration: (contact: string) => void;
  onBack: () => void;
}

type VerifyState = 'verifying' | 'success' | 'error';

export function VerifyScreen({ token, contact, onSuccess, onNeedRegistration, onBack }: VerifyScreenProps) {
  const [state, setState] = useState<VerifyState>('verifying');
  const [error, setError] = useState('');

  useEffect(() => {
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    if (!token) {
      setState('error');
      setError('No verification token provided');
      return;
    }

    setState('verifying');
    setError('');

    try {
      const response = await api.verifyMagicLink(token);
      
      if (response.success) {
        // Check if user needs to register (new user)
        if (response.needsRegistration) {
          toast.info('Welcome! Let\'s set up your account', {
            description: 'Just one more step to get started.',
          });
          onNeedRegistration(contact);
          return; // Don't proceed with login flow
        }
        
        // Existing user - proceed with login
        setState('success');
        toast.success('Welcome back to BtGtR! 🎉', {
          description: 'You have been successfully signed in.',
        });
        // Wait a moment to show success message before navigating
        setTimeout(() => {
          onSuccess();
        }, 1500);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Verification failed';
      
      // Check if error indicates user needs to register (fallback for old backend responses)
      if (errorMessage.toLowerCase().includes('not found') || 
          errorMessage.toLowerCase().includes('register')) {
        // Directly navigate to registration without showing intermediate screen
        toast.info('Welcome! Let\'s set up your account', {
          description: 'Just one more step to get started.',
        });
        onNeedRegistration(contact);
      } else {
        setState('error');
        setError(errorMessage);
        toast.error('Verification failed', {
          description: errorMessage,
        });
      }
    }
  };



  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50 flex items-center justify-center p-4 relative overflow-hidden">
      <FestiveDecoration />
      
      <Card className="w-full max-w-md border-2 border-emerald-100 shadow-xl relative z-10">
        <CardHeader className="text-center space-y-4 pb-6">
          <div className="flex justify-center mb-2">
            <LogoIcon size="lg" />
          </div>
          
          {state === 'verifying' && (
            <>
              <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-full flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
              </div>
              <CardTitle className="text-2xl">Verifying Your Link...</CardTitle>
              <CardDescription>Please wait while we sign you in</CardDescription>
            </>
          )}
          
          {state === 'success' && (
            <>
              <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <CardTitle className="text-2xl">Success! 🎉</CardTitle>
              <CardDescription>You're being signed in...</CardDescription>
            </>
          )}
          
          {state === 'error' && (
            <>
              <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <CardTitle className="text-2xl">Verification Failed</CardTitle>
              <CardDescription>We couldn't verify your magic link</CardDescription>
            </>
          )}
          

        </CardHeader>
        
        <CardContent className="space-y-4">
          {state === 'error' && (
            <>
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
              
              <div className="space-y-2">
                <Button
                  className="w-full"
                  onClick={verifyToken}
                >
                  Try Again
                </Button>
                
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={onBack}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Sign In
                </Button>
              </div>
            </>
          )}
          

          {state === 'verifying' && (
            <div className="text-center text-sm text-muted-foreground">
              This should only take a moment...
            </div>
          )}
          
          {state === 'success' && (
            <div className="text-center">
              <div className="inline-flex items-center gap-2 text-emerald-600">
                <CheckCircle2 className="w-5 h-5" />
                <span>Authentication successful!</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
